import { Users, Target, TrendingUp } from 'lucide-react'
import { motion } from 'framer-motion'
import { Sparkline } from '@/components/ui/Sparkline'
import { formatCurrency } from '@/utils/format-currency'
import type { Partner } from '@/types'

interface PartnersStatsCardsProps {
  partners: Partner[]
  sparklineData: number[]
  sparklineToPay: number[]
  sparklinePaid: number[]
}

export function PartnersStatsCards({
  partners,
  sparklineData,
  sparklineToPay,
  sparklinePaid,
}: PartnersStatsCardsProps) {
  const totalToPay = partners.reduce((acc, p) => acc + (p.stats?.totalToPay ?? 0), 0)
  const totalPaid = partners.reduce((acc, p) => acc + (p.stats?.totalPaid ?? 0), 0)

  return (
    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className='glass-card p-4 relative overflow-hidden'>
        <div className='absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-teal-500/40 to-transparent' />
        <div className='flex items-center justify-between mb-2'>
          <div className='w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-500'>
            <Users size={20} />
          </div>
          <Sparkline data={sparklineData} strokeColor='#14B8A6' fillColor='rgba(20,184,166,0.08)' />
        </div>
        <p className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-1' style={{ fontSize: 'var(--font-size-label)' }}>
          Total de Parceiros
        </p>
        <p className='text-2xl font-black text-[var(--font-color-body)]'>{partners.length}</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className='glass-card p-4 relative overflow-hidden'>
        <div className='absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-amber-500/40 to-transparent' />
        <div className='flex items-center justify-between mb-2'>
          <div className='w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500'>
            <Target size={20} />
          </div>
          <Sparkline data={sparklineToPay} strokeColor='#F59E0B' fillColor='rgba(245,158,11,0.08)' />
        </div>
        <p className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-1' style={{ fontSize: 'var(--font-size-label)' }}>
          Total a Pagar
        </p>
        <p className='text-2xl font-black text-amber-500'>{formatCurrency(totalToPay)}</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className='glass-card p-4 relative overflow-hidden'>
        <div className='absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent' />
        <div className='flex items-center justify-between mb-2'>
          <div className='w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400'>
            <TrendingUp size={20} />
          </div>
          <Sparkline data={sparklinePaid} strokeColor='#818CF8' fillColor='rgba(129,140,248,0.08)' />
        </div>
        <p className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-1' style={{ fontSize: 'var(--font-size-label)' }}>
          Total Pago
        </p>
        <p className='text-2xl font-black text-indigo-400'>{formatCurrency(totalPaid)}</p>
      </motion.div>
    </div>
  )
}
